import { useNavigate } from "react-router-dom";
import { ProductGlyph } from "../components/ProductGlyph";
import { ProductSummaryCard } from "../components/ProductSummaryCard";
import { SectionHeading } from "../components/SectionHeading";
import { SiteFooter } from "../components/SiteFooter";
import { StatusPill } from "../components/StatusPill";
import { Workflow } from "../components/Workflow";
import { pageMeta } from "../config/site";
import { liveProducts, plannedProducts, products } from "../data/products";
import { useDocumentTitle } from "../hooks/useDocumentTitle";
import { enterProduct, scrollToId } from "../lib/navigation";
import type { Product } from "../types";

export function HomePage() {
  const navigate = useNavigate();
  useDocumentTitle(pageMeta.home.title, pageMeta.home.description);

  function handleEnter(product: Product) {
    enterProduct(product);
  }

  return (
    <main data-screen-label="首页">
      <section className="home-hero">
        <div className="home-hero__grid"></div>
        <div className="home-hero__copy">
          <div className="hero-kicker">
            <span></span>会计师与税务师事务所的 AI 服务平台
          </div>
          <h1>
            从招标线索到专业交付，
            <br />
            <em>用 AI 为事务所持续加能</em>
          </h1>
          <p>
            加能财智连接招标信息、标书制作、审计作业与专业知识服务。多项专业产品可独立部署，让事务所从最需要的环节开始智能化。
          </p>
          <div className="hero-actions">
            <button
              className="button button--primary"
              type="button"
              onClick={() => scrollToId("live-products")}
            >
              查看产品与解决方案 <span>→</span>
            </button>
          </div>
          <div className="hero-trust">
            <span>AI 贯穿业务全流程</span>
            <span>产品可独立部署</span>
            <span>专业判断由人负责</span>
          </div>
        </div>
        <div className="home-hero__visual">
          <div className="hero-platform-card">
            <div className="hero-platform-card__head">
              <span>JIANENG INTELLIGENCE</span>
              <span>覆盖完整业务链</span>
            </div>
            <div className="hero-platform-card__statement">
              <h3>
                让信息、文档与专业经验，
                <br />
                沿着同一条业务链持续流动。
              </h3>
            </div>
            <div className="hero-platform-flow">
              <div>
                <div className="hero-platform-flow__top">
                  <span>
                    <small>智能标讯</small>
                    <strong>发现标讯</strong>
                  </span>
                  <span className="hero-platform-flow__icon">
                    <ProductGlyph type="bid" />
                  </span>
                </div>
                <p>聚合多源公告，按地区、业务类型与截止时间筛选可跟进机会</p>
              </div>
              <i>→</i>
              <div>
                <div className="hero-platform-flow__top">
                  <span>
                    <small>标书智作</small>
                    <strong>投标响应</strong>
                  </span>
                  <span className="hero-platform-flow__icon">
                    <ProductGlyph type="proposal" />
                  </span>
                </div>
                <p>识别资格、评分与材料要求，形成结构清晰的投标准备</p>
              </div>
              <i>→</i>
              <div>
                <div className="hero-platform-flow__top">
                  <span>
                    <small>审计智作</small>
                    <strong>审计协作</strong>
                  </span>
                  <span className="hero-platform-flow__icon">
                    <ProductGlyph type="audit" />
                  </span>
                </div>
                <p>统一项目计划、底稿执行与复核意见，清晰跟进审计协作进度</p>
              </div>
            </div>
            <div className="hero-platform-foundation">
              <span className="hero-platform-foundation__mark">
                <ProductGlyph type="jia" />
              </span>
              <div>
                <small>AI 能力底座</small>
                <strong>小加+ · 财税专业 AI 助手</strong>
              </div>
              <p>检索 · 整理 · 撰写 · 知识复用</p>
            </div>
          </div>
        </div>
      </section>

      <section id="real-work" className="pain-section page-section">
        <div className="section-index-line">
          <span>01</span>
          <span>真实工作</span>
        </div>
        <div className="pain-section__statement">
          <h2>
            事务所的专业能力，
            <br />
            不该被重复工作消耗
          </h2>
          <p>
            加能财智从事务所真实工作流程出发，把零散的信息、文档和经验转化为可以持续复用的业务能力。
          </p>
        </div>
        <div className="pain-list">
          <div>
            <span>标讯</span>
            <h3>招标信息分散</h3>
            <p>
              专业服务公告分散在不同渠道，团队需要反复检索与筛选，容易错过合适项目或关键截止时间。
            </p>
          </div>
          <div>
            <span>投标</span>
            <h3>投标要求复杂</h3>
            <p>
              招标文件篇幅长、要求交叉，资格条件、评分标准和响应材料需要逐项核对，准备耗时且容易遗漏。
            </p>
          </div>
          <div>
            <span>审计</span>
            <h3>项目协作有断点</h3>
            <p>
              项目计划、资料请求、底稿编制和复核意见分散在多个文件与沟通渠道，进度和责任难以统一跟进。
            </p>
          </div>
          <div>
            <span>知识</span>
            <h3>专业经验难复用</h3>
            <p>
              准则法规、项目经验和机构模板沉淀在个人文件中，新项目查找慢、复用难，知识价值无法持续放大。
            </p>
          </div>
        </div>
      </section>

      <section className="workflow-section page-section">
        <SectionHeading
          index="02"
          eyebrow="产品路径"
          title="围绕事务所关键业务环节，提供真正可用的 AI 产品"
          description="多项产品独立部署、按需进入；小加+作为专业 AI 能力逐步贯穿各环节。"
        />
        <Workflow />
      </section>

      <section id="live-products" className="live-products page-section">
        <SectionHeading
          index="03"
          eyebrow="已上线产品"
          title="从最需要的环节开始智能化"
          description="当前重点提供招标线索查找与标书制作辅助，两项产品均可独立使用。"
        />
        <div className="live-products__grid">
          {liveProducts.map((product) => (
            <ProductSummaryCard
              key={product.id}
              product={product}
              onEnter={handleEnter}
            />
          ))}
        </div>
      </section>

      <section id="planned-products" className="future-section">
        <div className="page-section">
          <SectionHeading
            index="04"
            eyebrow="规划中产品"
            title="面向审计项目协作与日常专业工作的两项规划产品"
            description="审计智作围绕计划分工、资料请求、底稿执行与复核意见协同组织项目作业；小加+面向准则法规检索、机构知识查找、资料整理与专业文档起草提供日常 AI 支持。"
          />
          <div className="future-grid">
            {plannedProducts.map((product) => (
              <article
                id={`product-${product.id}`}
                className={`future-card future-card--${product.id}`}
                key={product.id}
              >
                <div className="future-card__top">
                  <span className="future-symbol">
                    <ProductGlyph type={product.id} />
                  </span>
                  <StatusPill product={product} />
                </div>
                <span className="future-eyebrow">{product.eyebrow}</span>
                <h3>{product.name}</h3>
                {product.id !== "audit" ? <h4>{product.plainName}</h4> : null}
                <p>{product.description}</p>
                <div className="future-capabilities">
                  {product.capabilities.map((item) => (
                    <span key={item}>{item}</span>
                  ))}
                </div>
              </article>
            ))}
          </div>
        </div>
      </section>

      <section id="deployment" className="deployment-section page-section">
        <div className="section-index-line">
          <span>05</span>
          <span>部署与治理</span>
        </div>
        <div className="deployment-section__copy">
          <h2>多项产品独立部署，适配事务所的信息合规需求</h2>
          <p>
            智能标讯、标书智作等产品可以独立启用，事务所按业务范围、成员角色和项目需要组织使用；数据存储、保留与模型服务边界随产品清晰说明。
          </p>
        </div>
        <div
          className="deployment-assurance"
          aria-label="独立部署与信息合规设计原则"
        >
          <div className="deployment-assurance__head">
            <div>
              <span>DEPLOYMENT & GOVERNANCE</span>
              <strong>从一个业务环节开始，逐步形成机构级 AI 能力</strong>
            </div>
            <p>
              按产品启用、按角色授权、按规则披露，让技术能力与事务所管理要求同步落地。
            </p>
          </div>
          <div className="deployment-assurance__grid">
            <article>
              <span className="deployment-assurance__icon">
                <img src="/assets/deployment-icon-independent.svg" alt="" />
              </span>
              <div>
                <small>按需启用</small>
                <strong>产品可独立使用</strong>
                <p>
                  智能标讯、标书智作等产品可分别启用，从当前最需要的业务环节开始，后续再按团队节奏扩展。
                </p>
              </div>
            </article>
            <article>
              <span className="deployment-assurance__icon">
                <img src="/assets/deployment-icon-access.svg" alt="" />
              </span>
              <div>
                <small>组织协作</small>
                <strong>机构与项目权限清晰</strong>
                <p>
                  按机构、成员和项目设置访问范围，团队在授权空间内协作，减少资料在无关人员之间流转。
                </p>
              </div>
            </article>
            <article>
              <span className="deployment-assurance__icon">
                <img src="/assets/deployment-icon-governance.svg" alt="" />
              </span>
              <div>
                <small>清晰披露</small>
                <strong>数据处理边界可查</strong>
                <p>
                  每项产品清晰说明存储、保留、模型服务与第三方使用情况，便于事务所完成内部合规评估。
                </p>
              </div>
            </article>
          </div>
        </div>
      </section>

      <section id="professional-control" className="security-preview page-section">
        <div className="section-index-line">
          <span>06</span>
          <span>安全与专业边界</span>
        </div>
        <div className="security-preview__visual">
          <div className="security-blueprint">
            <div className="security-blueprint__label">
              <span></span>PROFESSIONAL CONTROL
            </div>
            <div className="security-decision-layer">
              <span className="security-decision-layer__icon">
                <ProductGlyph type="human" />
              </span>
              <div>
                <small>最终决策层</small>
                <strong>专业人员判断与复核</strong>
                <p>关键结论由具备相应职责的人员作出</p>
              </div>
              <em>HUMAN IN CONTROL</em>
            </div>
            <div className="security-assist-graph">
              <div className="security-assist-graph__head">
                <span>AI 辅助</span>
                <em>辅助成果提交上方专业人员判断与复核 ↑</em>
              </div>
              <div className="security-assist-graph__flow">
                <div className="assist-graph-node assist-graph-node--source">
                  <small>受控输入</small>
                  <strong>
                    业务资料
                    <br />
                    机构知识
                  </strong>
                </div>
                <i>→</i>
                <div className="assist-graph-core">
                  <span>检索</span>
                  <span>整理</span>
                  <span>分析</span>
                  <span>文档辅助</span>
                </div>
                <i>→</i>
                <div className="assist-graph-node assist-graph-node--output">
                  <small>辅助输出</small>
                  <strong>
                    线索 · 结构
                    <br />
                    工作草稿
                  </strong>
                </div>
              </div>
            </div>
            <div className="security-guardrails">
              <div>
                <span>
                  <ProductGlyph type="boundary" />
                </span>
                <strong>数据边界</strong>
                <em>明确处理范围</em>
              </div>
              <div>
                <span>
                  <ProductGlyph type="access" />
                </span>
                <strong>访问控制</strong>
                <em>按角色授权</em>
              </div>
              <div>
                <span>
                  <ProductGlyph type="retention" />
                </span>
                <strong>使用与保留</strong>
                <em>按政策处理</em>
              </div>
              <div>
                <span>
                  <ProductGlyph type="review" />
                </span>
                <strong>人工复核</strong>
                <em>专业人员把关</em>
              </div>
            </div>
          </div>
        </div>
        <div className="security-preview__copy">
          <h2>
            AI 提升效率，
            <br />
            专业判断始终由人负责
          </h2>
          <p>
            围绕数据边界、访问控制、使用与保留以及人工复核，建立清晰可理解的责任边界，让团队在开始使用前了解处理范围。
          </p>
          <button
            className="button button--ink"
            type="button"
            onClick={() => navigate("/security")}
          >
            查看安全与合规 <span>→</span>
          </button>
        </div>
      </section>

      <section className="closing-cta">
        <div>
          <span>从下一个项目开始</span>
          <h2>为团队加能</h2>
        </div>
        <p>
          更快发现值得投入的机会，更高效完成投标准备，把团队时间留给客户沟通和专业判断。
        </p>
        <div className="closing-cta__actions">
          <button
            className="button button--energy"
            type="button"
            onClick={() => handleEnter(products[0])}
          >
            查找招标线索 →
          </button>
          <button
            className="button button--outline-light"
            type="button"
            onClick={() => handleEnter(products[1])}
          >
            进入标书智作 →
          </button>
        </div>
      </section>
      <SiteFooter />
    </main>
  );
}
